import { useState } from "react";
import type { ReactNode } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { CustomButton } from "../common/customButton";
import { formatDateTime } from "../../utils/formatDate";
import type { ApiResponse } from "../../services/genesisApi/httpsMethods";
import {
  hawkErrorMessage,
  pushDealToHawk,
  requestHawkApproval,
  setAssetManagerReview,
  setDealLeadReview,
  sendUpdateToHawk,
} from "../../services/hawkReviewApi";
import type {
  HawkApproval,
  HawkStage,
  HawkStatus,
} from "../../services/hawkReviewApi";

type SendToHawkDialogProps = {
  open: boolean;
  dealId: number;
  status: HawkStatus | null;
  loading: boolean;
  /** Set when the status couldn't be loaded. */
  loadError: string | null;
  /** May the user resend the request email (any editor). */
  canEdit: boolean;
  onRetry: () => void;
  onClose: () => void;
  /** Called after any successful action so the parent reloads the status. */
  onChanged: (action: HawkAction) => void;
};

export type HawkAction =
  | "request"
  | "resend"
  | "deal_lead"
  | "asset_manager"
  | "send"
  | "update";

const ACTIVE_STEP: Record<HawkStage, number> = {
  not_started: 0,
  awaiting_deal_lead: 1,
  awaiting_asset_manager: 2,
  ready_to_send: 3,
  in_hawk: 4,
};

const mutedSx = { color: "text.secondary" } as const;

function personName(name: string | null | undefined, fallback: string) {
  return name?.trim() || fallback;
}

function approvalText(approval: HawkApproval) {
  const when = approval.at ? ` on ${formatDateTime(approval.at)}` : "";
  return approval.by
    ? `Approved by ${personName(approval.by.name, "a former user")}${when}.`
    : `Approved${when}.`;
}

function assetManagerNames(status: HawkStatus) {
  const names = status.asset_managers.map((am) => personName(am.name, am.email));
  return names.length ? names.join(" or ") : "an asset manager";
}

/** Two-click confirm for actions that change Hawk. */
function ConfirmRow({
  text,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  text: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Alert severity="warning" sx={{ mt: 1 }}>
      <Typography variant="body2">{text}</Typography>
      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        <CustomButton
          handleButtonPress={onConfirm}
          text={confirmLabel}
          solid
          isButtonDisabled={busy}
          isLoading={busy}
          isLoadingText="Sending..."
        />
        <CustomButton
          handleButtonPress={onCancel}
          text="Cancel"
          solid={false}
          isButtonDisabled={busy}
        />
      </Box>
    </Alert>
  );
}

/**
 * Send to Hawk: request approval, deal lead approval, asset manager approval,
 * then send. The server decides the stage and what this user may do; this
 * dialog only shows it.
 */
const SendToHawkDialog = ({
  open,
  dealId,
  status,
  loading,
  loadError,
  canEdit,
  onRetry,
  onClose,
  onChanged,
}: SendToHawkDialogProps) => {
  const [busy, setBusy] = useState<HawkAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<"send" | "update" | null>(
    null,
  );

  const handleClose = () => {
    if (busy) return;
    setError(null);
    setNotice(null);
    setConfirming(null);
    onClose();
  };

  const run = async <T,>(
    action: HawkAction,
    call: () => Promise<ApiResponse<T>>,
    fallbackError: string,
    successNotice: (data: T | undefined) => string | null,
  ) => {
    setBusy(action);
    setError(null);
    setNotice(null);
    try {
      const response = await call();
      if (!response.success) {
        setError(hawkErrorMessage(response, fallbackError));
        return;
      }
      setConfirming(null);
      setNotice(successNotice(response.data));
      onChanged(action);
    } finally {
      setBusy(null);
    }
  };

  const leadName = personName(status?.deal_lead?.name, "the deal lead");
  const perms = status?.permissions;
  const stage = status?.stage;

  const requestStep = (): ReactNode => {
    if (!status) return null;
    if (status.requested) {
      const by = status.requested.by
        ? ` by ${personName(status.requested.by.name, "a former user")}`
        : "";
      const when = status.requested.at
        ? ` on ${formatDateTime(status.requested.at)}`
        : "";
      const to = status.requested.sent_to
        ? ` Email sent to ${status.requested.sent_to}.`
        : "";
      return `Requested${by}${when}.${to}`;
    }
    return `Emails ${leadName} a link to this deal and asks them to approve it.`;
  };

  const handleRequest = (resend: boolean) =>
    run(
      resend ? "resend" : "request",
      () => requestHawkApproval(dealId, resend),
      "Couldn't request Hawk approval.",
      (data) =>
        data?.sent_to ? `Email sent to ${data.sent_to}.` : "Approval requested.",
    );

  const handleDealLead = (approved: boolean) =>
    run(
      "deal_lead",
      () => setDealLeadReview(dealId, approved),
      "Couldn't save the deal lead approval.",
      () =>
        approved
          ? "Approved. The asset managers have been emailed."
          : "Approval withdrawn.",
    );

  const handleAssetManager = (approved: boolean) =>
    run(
      "asset_manager",
      () => setAssetManagerReview(dealId, approved),
      "Couldn't save the asset manager approval.",
      () =>
        approved
          ? "Approved. The deal can now be sent to Hawk."
          : "Approval withdrawn.",
    );

  const handleSend = () =>
    run(
      "send",
      () => pushDealToHawk(dealId),
      "Couldn't send the deal to Hawk.",
      () => "Sent to Hawk.",
    );

  const handleSendUpdate = () =>
    run(
      "update",
      () => sendUpdateToHawk(dealId),
      "Couldn't send the update to Hawk.",
      (data) =>
        data?.staged === false
          ? "Update sent and applied in Hawk."
          : "Update sent. A Hawk manager will review it before anything changes.",
    );

  const lastSync = status?.last_sync;
  const lastSyncText = lastSync
    ? `Last ${lastSync.scope === "full" ? "send" : "update"}: ` +
      `${lastSync.status === "success" ? "succeeded" : "failed"} on ` +
      `${formatDateTime(lastSync.created_at)}` +
      (lastSync.status === "failed" && lastSync.error_message
        ? ` - ${lastSync.error_message}`
        : "")
    : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="send-to-hawk-title"
    >
      <DialogTitle id="send-to-hawk-title">Send to Hawk</DialogTitle>
      <DialogContent>
        {loading && !status ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} aria-label="Loading Hawk status" />
          </Box>
        ) : loadError && !status ? (
          <Alert
            severity="error"
            action={
              <CustomButton
                handleButtonPress={onRetry}
                text="Retry"
                solid={false}
                isButtonDisabled={loading}
              />
            }
          >
            {loadError}
          </Alert>
        ) : status ? (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {notice && !error && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {notice}
              </Alert>
            )}
            {status.blocking_reason && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {status.blocking_reason}
              </Alert>
            )}

            <Stepper
              activeStep={ACTIVE_STEP[status.stage]}
              orientation="vertical"
            >
              <Step completed={!!status.requested}>
                <StepLabel
                  optional={
                    status.requested ? (
                      <Typography variant="caption" sx={mutedSx}>
                        {requestStep()}
                      </Typography>
                    ) : undefined
                  }
                >
                  Request approval
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={mutedSx}>
                    {requestStep()}
                  </Typography>
                  {perms?.can_request && (
                    <Box sx={{ mt: 1 }}>
                      <CustomButton
                        handleButtonPress={() => void handleRequest(false)}
                        text="Request approval"
                        solid
                        isButtonDisabled={!!busy}
                        isLoading={busy === "request"}
                        isLoadingText="Sending..."
                      />
                    </Box>
                  )}
                </StepContent>
              </Step>

              <Step completed={status.deal_lead_approval.approved}>
                <StepLabel
                  optional={
                    status.deal_lead_approval.approved ? (
                      <Typography variant="caption" sx={mutedSx}>
                        {approvalText(status.deal_lead_approval)}
                      </Typography>
                    ) : undefined
                  }
                >
                  Deal lead approval ({leadName})
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={mutedSx}>
                    {perms?.can_review_deal_lead
                      ? "Check the deal details, then approve it for Hawk."
                      : `Waiting for ${leadName} to approve.`}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                    {perms?.can_review_deal_lead && (
                      <CustomButton
                        handleButtonPress={() => void handleDealLead(true)}
                        text="Approve"
                        solid
                        isButtonDisabled={!!busy}
                        isLoading={busy === "deal_lead"}
                        isLoadingText="Saving..."
                      />
                    )}
                    {canEdit && stage === "awaiting_deal_lead" && (
                      <CustomButton
                        handleButtonPress={() => void handleRequest(true)}
                        text="Resend email"
                        solid={false}
                        isButtonDisabled={!!busy}
                        isLoading={busy === "resend"}
                        isLoadingText="Sending..."
                      />
                    )}
                  </Box>
                </StepContent>
              </Step>

              <Step completed={status.asset_manager_approval.approved}>
                <StepLabel
                  optional={
                    status.asset_manager_approval.approved ? (
                      <Typography variant="caption" sx={mutedSx}>
                        {approvalText(status.asset_manager_approval)}
                      </Typography>
                    ) : undefined
                  }
                >
                  Asset manager approval
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={mutedSx}>
                    {perms?.can_review_asset_manager
                      ? `${leadName} has approved. Check the deal, then approve it for Hawk.`
                      : `Waiting for ${assetManagerNames(status)} to approve.`}
                  </Typography>
                  {perms?.can_review_asset_manager && (
                    <Box sx={{ mt: 1 }}>
                      <CustomButton
                        handleButtonPress={() => void handleAssetManager(true)}
                        text="Approve"
                        solid
                        isButtonDisabled={!!busy}
                        isLoading={busy === "asset_manager"}
                        isLoadingText="Saving..."
                      />
                    </Box>
                  )}
                </StepContent>
              </Step>

              <Step completed={stage === "in_hawk"}>
                <StepLabel
                  optional={
                    stage === "in_hawk" && status.hawk_loan_id ? (
                      <Typography variant="caption" sx={mutedSx}>
                        Hawk loan {status.hawk_loan_id}
                      </Typography>
                    ) : undefined
                  }
                >
                  Send to Hawk
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={mutedSx}>
                    {perms?.can_send
                      ? "Both approvals are done. Sending creates the loan in Hawk."
                      : "Both approvals are done. Anyone with edit access can send it."}
                  </Typography>
                  {perms?.can_send &&
                    (confirming === "send" ? (
                      <ConfirmRow
                        text="This creates the loan in Hawk. You can't send it again from Badger."
                        confirmLabel="Send now"
                        busy={busy === "send"}
                        onConfirm={() => void handleSend()}
                        onCancel={() => setConfirming(null)}
                      />
                    ) : (
                      <Box sx={{ mt: 1 }}>
                        <CustomButton
                          handleButtonPress={() => setConfirming("send")}
                          text="Send to Hawk"
                          solid
                          isButtonDisabled={!!busy}
                        />
                      </Box>
                    ))}
                </StepContent>
              </Step>
            </Stepper>

            {(perms?.can_review_deal_lead &&
              status.deal_lead_approval.approved) ||
            (perms?.can_review_asset_manager &&
              status.asset_manager_approval.approved) ? (
              <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                {perms?.can_review_deal_lead &&
                  status.deal_lead_approval.approved && (
                    <CustomButton
                      handleButtonPress={() => void handleDealLead(false)}
                      text="Withdraw my approval"
                      solid={false}
                      isButtonDisabled={!!busy}
                      isLoading={busy === "deal_lead"}
                      isLoadingText="Saving..."
                    />
                  )}
                {perms?.can_review_asset_manager &&
                  status.asset_manager_approval.approved && (
                    <CustomButton
                      handleButtonPress={() => void handleAssetManager(false)}
                      text="Withdraw asset manager approval"
                      solid={false}
                      isButtonDisabled={!!busy}
                      isLoading={busy === "asset_manager"}
                      isLoadingText="Saving..."
                    />
                  )}
              </Box>
            ) : null}

            {stage === "in_hawk" && (
              <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                {status.hawk_loan_url && (
                  <Link
                    href={status.hawk_loan_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                  >
                    Open in Hawk
                  </Link>
                )}
                {lastSyncText && (
                  <Typography variant="body2" sx={mutedSx}>
                    {lastSyncText}
                  </Typography>
                )}
                {perms?.can_send_update &&
                  (confirming === "update" ? (
                    <ConfirmRow
                      text="Sends the deal's current data to Hawk, e.g. after an add-on closes. A Hawk manager reviews the changes before anything is applied."
                      confirmLabel="Send update"
                      busy={busy === "update"}
                      onConfirm={() => void handleSendUpdate()}
                      onCancel={() => setConfirming(null)}
                    />
                  ) : (
                    <Box>
                      <CustomButton
                        handleButtonPress={() => setConfirming("update")}
                        text="Send update to Hawk"
                        solid={false}
                        isButtonDisabled={!!busy}
                      />
                    </Box>
                  ))}
              </Box>
            )}
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <CustomButton
          handleButtonPress={handleClose}
          text="Close"
          solid={false}
          widthOverride="100px"
          isButtonDisabled={!!busy}
        />
      </DialogActions>
    </Dialog>
  );
};

export default SendToHawkDialog;
